// ============================================================================
// Lecture de l'en-tete Host : la SEULE source de verite sur les hotes Vyndra
// ----------------------------------------------------------------------------
// POURQUOI CE FICHIER EXISTE, et pourquoi il est minuscule et pur.
//
// Quatre hotes servent la meme application :
//
//   app.vyndra.fr                  le back-office de gestion (« Vyndra »)
//   portail.vyndra.fr              l'espace salarie
//   guardinformatique.vyndra.fr    un univers metier
//   guardsecurity.vyndra.fr        un autre univers metier
//
// Trois choses TRES differentes ont besoin de decoder cet en-tete :
//   - la resolution d'un univers par son adresse (api/espace/_resolution.ts) ;
//   - le domaine sur lequel poser les cookies de session (lib/cookies-session.ts) ;
//   - la fabrication des adresses d'espace (components/espace/espace-adresse.ts).
//
// Ecrire la regle trois fois, c'est la voir diverger au premier correctif. Elle
// vit donc ici, une seule fois, sans aucun import : ce module doit etre
// utilisable dans le middleware (runtime edge), dans un composant serveur, et
// dans le navigateur.
//
// AUCUN nom de domaine n'est ecrit en dur. « vyndra.fr » n'apparait nulle part :
// le domaine parent est DEDUIT de l'hote d'arrivee. Le jour ou l'application
// tourne sur un autre domaine, rien ici ne change.
// ============================================================================

/**
 * Etiquettes qui ne designent JAMAIS un univers metier.
 *
 * `app` et `portail` sont des hotes de l'application. `api` et `www` ne servent
 * pas cette application du tout. Les trois derniers attrapent les hotes
 * techniques : `localhost`, et les adresses IP dont la premiere etiquette est
 * numerique (sondes de sante, verifications de deploiement).
 */
export const ETIQUETTES_HORS_ESPACE: ReadonlySet<string> = new Set([
  'app',
  'api',
  'portail',
  'www',
  'localhost',
  '127',
  '0',
]);

/** Etiquette de l'hote qui sert le back-office Vyndra. */
export const ETIQUETTE_VYNDRA = 'app';

/** Etiquette de l'hote qui sert le portail salarie. */
export const ETIQUETTE_PORTAIL = 'portail';

/** Retire le port et met en minuscules. Rend une chaine vide si rien d'exploitable. */
function nettoyerHote(hote: string | null | undefined): string {
  const brut = (hote ?? '').trim().toLowerCase();
  if (brut.length === 0) return '';
  return brut.split(':')[0] ?? '';
}

/** Vrai pour une adresse IPv4 nue, qui n'est jamais un nom d'hote a etiquettes. */
function estAdresseNumerique(hoteSansPort: string): boolean {
  return /^[0-9.]+$/.test(hoteSansPort);
}

/**
 * Reduit un hote a sa premiere etiquette : « guardsecurity » dans
 * « guardsecurity.vyndra.fr:443 ».
 *
 * Rend null quand l'hote ne peut pas designer un univers : etiquette reservee,
 * adresse IP, ou nom sans point (« localhost », un nom de conteneur Docker).
 */
export function etiquetteDeLHote(hote: string | null | undefined): string | null {
  const sansPort = nettoyerHote(hote);
  if (sansPort.length === 0) return null;
  if (estAdresseNumerique(sansPort)) return null;
  const etiquette = sansPort.split('.')[0] ?? '';
  if (etiquette.length === 0) return null;
  if (ETIQUETTES_HORS_ESPACE.has(etiquette)) return null;
  // Un hote sans point ne peut pas etre un sous-domaine : on ne tente rien.
  if (!sansPort.includes('.')) return null;
  return etiquette;
}

/**
 * Domaine sur lequel un cookie doit etre pose pour que TOUS les hotes de
 * l'application le partagent : « .vyndra.fr » pour « app.vyndra.fr ».
 *
 * Rend null quand il n'y a rien a elargir, et c'est le cas qui protege le
 * developpement local :
 *   - « localhost », « localhost:3000 », un nom de conteneur : aucun point,
 *     donc aucun sous-domaine possible ;
 *   - une adresse IP : les cookies de domaine y sont interdits par les
 *     navigateurs ;
 *   - un domaine nu a deux etiquettes (« vyndra.fr ») : il n'y a pas de place
 *     pour un sous-domaine au-dessus, le cookie reste propre a l'hote.
 *
 * Dans tous ces cas, l'appelant garde le comportement d'aujourd'hui : un cookie
 * propre a l'hote. Personne ne perd sa session sur son poste de travail.
 */
export function domaineDePartage(hote: string | null | undefined): string | null {
  const sansPort = nettoyerHote(hote);
  if (sansPort.length === 0) return null;
  if (!sansPort.includes('.')) return null;
  if (estAdresseNumerique(sansPort)) return null;
  const etiquettes = sansPort.split('.').filter((e) => e.length > 0);
  if (etiquettes.length < 3) return null;
  return `.${etiquettes.slice(1).join('.')}`;
}

/**
 * Vrai quand cet hote est CANDIDAT a servir un univers metier.
 *
 * « Candidat » et pas « sert » : la reponse ne dit pas qu'un univers porte cette
 * adresse, seulement que la forme de l'hote le permet. Seul le serveur sait si
 * l'adresse correspond a un univers, et lui seul dit qui a le droit d'y entrer
 * (voir api/espace/_resolution.ts et le garde-fou de components/espace/espace-shell.tsx).
 *
 * Sert a choisir une DESTINATION, jamais a accorder un acces.
 */
export function estHoteCandidatEspace(hote: string | null | undefined): boolean {
  return etiquetteDeLHote(hote) !== null;
}

/**
 * Ou l'on arrive apres s'etre connecte, decide par l'HOTE et par rien d'autre.
 *
 * Sur un hote d'univers, l'ecran de choix n'a aucun sens : l'adresse a deja
 * choisi. On envoie a la racine, que le middleware sert comme l'accueil de cet
 * univers (`NextResponse.rewrite` vers /hub/<slug>, donc l'URL reste la racine,
 * ce que le manifeste de l'application installee declare comme point de depart).
 *
 * Sur app.vyndra.fr, on garde le lanceur : il n'impose aucun clic superflu et
 * n'envoie plus personne sur le tableau de bord comptable.
 *
 * CE N'EST PAS UN DROIT. La racine d'un hote d'univers ne s'ouvre que si le
 * serveur reconnait l'appartenance : le middleware exige la session, et
 * l'accueil de l'espace refuse net quand /me/spaces ne liste pas cet univers
 * (components/espace/espace-shell.tsx). Une adresse devinee n'ouvre rien.
 *
 * Et c'est une destination SURE dans tous les cas : sur app.vyndra.fr comme sur
 * un hote inconnu, la racine avec session redirige vers le lanceur.
 */
export function destinationApresConnexion(hote: string | null | undefined): string {
  return estHoteCandidatEspace(hote) ? '/' : '/lancer';
}

/**
 * Recompose un hote complet a partir d'une etiquette et de l'hote courant :
 * (« guardsecurity », « app.vyndra.fr ») donne « guardsecurity.vyndra.fr ».
 *
 * Rend null quand l'hote courant n'a pas de domaine parent exploitable, donc en
 * developpement local. L'appelant retombe alors sur un chemin.
 */
export function hoteAvecEtiquette(
  etiquette: string,
  hoteCourant: string | null | undefined,
): string | null {
  const propre = etiquette.trim().toLowerCase();
  if (propre.length === 0) return null;
  const domaine = domaineDePartage(hoteCourant);
  if (domaine === null) return null;
  // domaineDePartage rend « .vyndra.fr » : on retire le point de tete.
  return `${propre}${domaine}`;
}
